import React, { useMemo, useState } from 'react';
import { useCurrentProject } from '@material-bank/projects-utils';
import { FileList } from './FileList';
import { FileSelector } from './FileSelector';
import { useDownload } from './hooks/useDownload';
import { useFileSection } from './hooks/useFileSection';
import { usePreviewFilesDialog } from './hooks/usePreviewFilesDialog';
import { PreviewFilesActions } from './PreviewFilesActions';
import { PreviewFilesDialog } from './PreviewFilesDialog';
import { PreviewFileViewer } from './PreviewFileViewer';
import { Title } from './Title';
import {
  getBearer,
  fetchAttachments,
  AttachmentsResponse,
  fetchProductBySku,
  ProductResponse,
} from '../../../../../../../UILibrary';
import { getMaterialsByKeyList } from '../../../utils';
import { PreviewFilesDialogFooter } from '../PreviewFilesDialogFooter';
import { ProductDetails } from './previewFiles.types';
import { ProductDetailsSection } from './ProductDetailsSection';

interface OpenPreviewFilesDialogCustomEvent {
  selectedFileIdx?: number;
  selectedFileId?: number;
  productId?: string; // The ID of the product.
  productSku?: string; // The SKU of the product to fetch attachments for. Must be parent sku if configurable!
  thumbnailProduct?: {
    productName: string;
    brandName: string;
    productUrl: string;
    productImage: string;
  };
}

export const PreviewFiles = () => {
  const { currentProject, setCurrentProjectId } = useCurrentProject();

  // Event data
  const [productSku, setProductSku] = useState<string | null>(null);

  // Local data
  const [isFetchingFiles, setIsFetchingFiles] = useState(false);
  const [filesResponse, setFilesResponse] =
    useState<AttachmentsResponse | null>(null);

  const {
    isSelectingFiles,
    checkedFileMap,
    setIsSelectingFiles,
    setCheckedFileMap,
    onCancelSelectFiles,
  } = useFileSection();
  const [product, setProduct] = useState<ProductResponse | null>(null);

  // Product details
  const [productDetails, setProductDetails] = useState<ProductDetails | null>(
    null,
  );

  // File preview
  const [selectedFileIndex, setSelectedFileIndex] = useState<number | null>(
    null,
  );

  // PDF viewing
  const [_, setZoomLevel] = useState<number>(1.0);

  const { downloadClickHandler } = useDownload();

  // Single-file select & Preview
  const selectedFile = useMemo(() => {
    if (filesResponse?.items && selectedFileIndex !== null) {
      return filesResponse.items[selectedFileIndex];
    }

    return null;
  }, [filesResponse, selectedFileIndex]);

  const totalFilesChecked = useMemo(() => {
    return Object.values(checkedFileMap).filter((v) => v === true).length;
  }, [checkedFileMap]);

  const openPreviewFilesDialog = async (event: {
    detail: OpenPreviewFilesDialogCustomEvent;
  }) => {
    const selectedFileIdx = event.detail?.selectedFileIdx || 0;
    const selectedFileId = event.detail?.selectedFileId;

    // Set events (product id and sku)
    const productId =
      event?.detail?.productId ??
      window.productAttributeJson?.product_id?.value ??
      null;

    const productSku =
      event?.detail?.productSku ??
      window.productAttributeJson?.sku?.value ??
      null;
    setProductSku(productSku);

    if (typeof selectedFileIdx === 'number') {
      setSelectedFileIndex(selectedFileIdx);
    }

    // setIsOpen(true);
    const bearerToken = getBearer();
    setIsFetchingFiles(true);

    fetchProductBySku(productSku).then((response) => {
      setProduct(response);
    });

    try {
      const [attachmentsResponse, materialInfoResponse] = await Promise.all([
        fetchAttachments(bearerToken, productSku),
        // TODO: refactor without type assertion, or add comment
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
        getMaterialsByKeyList([productId as string]),
      ]);

      if (
        !!materialInfoResponse?.results &&
        materialInfoResponse?.results?.length > 0
      ) {
        const product = materialInfoResponse.results[0];
        const thumbnailImageUrl = product.thumbnailImageUrl;
        const name = product.name;
        const manufacturer = product.manufacturer;
        const url = product.url;

        setProductDetails({
          thumbnailImageUrl,
          categories: product.categories,
          name,
          manufacturer,
          url,
          productSku,
        });
      } else if (!!event.detail?.thumbnailProduct) {
        const thumbnailImageUrl = event.detail?.thumbnailProduct?.productImage;
        const name = event.detail?.thumbnailProduct?.productName;
        const manufacturer = event.detail?.thumbnailProduct?.brandName;
        const url = event.detail?.thumbnailProduct?.productUrl;

        setProductDetails({
          thumbnailImageUrl,
          categories: [],
          name,
          manufacturer,
          url,
          productSku,
        });
      } else {
        setProductDetails(null);
      }

      setFilesResponse(attachmentsResponse);

      if (!selectedFileIdx && !!selectedFileId) {
        const foundIndexFromFileId =
          attachmentsResponse.items.findIndex((file) => {
            return file.attachment_id === selectedFileId;
          }) || 0;

        // set first file if not found
        setSelectedFileIndex(foundIndexFromFileId);
      } else if (!!selectedFileIdx && !!selectedFileId) {
        setSelectedFileIndex(0);
      }
    } finally {
      setIsFetchingFiles(false);
    }

    document.body.style.overflow = 'hidden';
    setZoomLevel(1.0);
  };

  const { isOpen, closePreviewFileDialog } = usePreviewFilesDialog(
    openPreviewFilesDialog,
  );

  const onFileClicked = (idx: number) => {
    if (selectedFileIndex === idx) {
      // Uncheck
      setSelectedFileIndex(null);
    } else {
      setSelectedFileIndex(idx);
      setZoomLevel(1.0);
    }
  };

  const onSelectFilesClicked = () => {
    setIsSelectingFiles(true);
  };

  const resetState = () => {
    setIsSelectingFiles(false);
    setCheckedFileMap({});
    setSelectedFileIndex(null);
    setZoomLevel(1.0);
  };

  function onDownload() {
    downloadClickHandler({
      isSelectingFiles,
      selectedFiles,
      selectedFile,
      productDetails,
      productSku,
      product,
      onDownloadComplete: () => {
        resetState();
        closePreviewFileDialog();
      },
    });
  }

  const selectedFiles = useMemo(() => {
    if (filesResponse && filesResponse.items?.length >= 1) {
      return filesResponse?.items.filter((item, idx) => {
        return checkedFileMap[idx];
      });
    } else {
      return [];
    }
  }, [filesResponse, checkedFileMap]);

  return (
    <PreviewFilesDialog isOpen={isOpen} onClose={closePreviewFileDialog}>
      <div className="flex flex-col gap-2 py-4 px-4 sm:px-6">
        <Title onClose={closePreviewFileDialog} />
        <FileSelector
          selectedFileIndex={selectedFileIndex}
          items={filesResponse?.items}
          onChange={(value) => {
            setSelectedFileIndex(parseInt(value));
            setZoomLevel(1.0); // Reset zoom when changing files
          }}
        />
      </div>
      {/* Mobile */}
      <div className="flex sm:hidden overflow-hidden border-t border-t-[#F2F2F2] flex-1">
        <div className="flex flex-1 items-center justify-center px-6 pb-10 bg-[#F5F5F7] rounded-b-2xl">
          <PreviewFileViewer file={selectedFile} />
        </div>
      </div>
      {/* Desktop */}
      <div className="hidden sm:flex overflow-hidden border-t border-t-[#F2F2F2] flex-1">
        <div
          className="overflow-hidden flex flex-col h-full"
          style={{ width: '370px', flexShrink: 0 }}
        >
          {productDetails?.thumbnailImageUrl && (
            <ProductDetailsSection productDetails={productDetails} />
          )}
          {productDetails?.thumbnailImageUrl && (
            <hr className="border-t border-[#F2F2F2] mx-6" />
          )}
          <FileList
            isLoading={isFetchingFiles}
            isSelectingFiles={isSelectingFiles}
            items={filesResponse?.items}
            checkedFileMap={checkedFileMap}
            selectedFileIndex={selectedFileIndex}
            onFileClick={onFileClicked}
            onCheckboxClick={(idx) => {
              const fileMap = Object.assign({}, checkedFileMap);
              fileMap[idx] = !fileMap[idx];
              setCheckedFileMap(fileMap);
            }}
          />
          <PreviewFilesActions
            isSelectingFiles={isSelectingFiles}
            isDownloadDisabled={selectedFileIndex === null}
            totalFilesChecked={totalFilesChecked}
            onSelectFile={onSelectFilesClicked}
            onCancelSelect={onCancelSelectFiles}
            downloadClickHandler={onDownload}
          />
        </div>
        <div className="flex flex-1 items-center justify-center px-6 pb-10 bg-[#F5F5F7] rounded-br-2xl">
          <PreviewFileViewer file={selectedFile} />
        </div>
      </div>
      <PreviewFilesDialogFooter
        currentProject={currentProject}
        totalFilesChecked={totalFilesChecked}
        isDownloadDisabled={selectedFileIndex === null}
        onProjectChange={(projectId) => {
          setCurrentProjectId(projectId);
        }}
        onDonwnload={onDownload}
      />
    </PreviewFilesDialog>
  );
};
